/**
 * Block 12 Hotfix-2C Phase C5 Step 11 — IDB autosave layer для anketa drafts.
 *
 * Persistent mirror Zustand store'а из Step 10 (`useCalculatorStore`). Хранит
 * черновики анкет keyed by `CalculatorAnketaType` — переживает F5 refresh +
 * закрытие/открытие браузера + cross-tab (last-writer-wins).
 *
 * **Отдельная DB `calculator-drafts`** — не пересекается с existing
 * `pomoshnik-offline-v1` (4 store: methodologies/projects/quotes/pendingMedia).
 * Разные DB по двум причинам:
 *   1. Schema isolation — добавление/удаление calculator drafts store не
 *      требует version bump main DB (которая хранит data долговременных
 *      проектов, КП и blob queue).
 *   2. Lifecycle isolation — `clearAllDraftsFromIdb()` для logout-style
 *      сброса не трогает saved projects (они принадлежат другому слою —
 *      `useAnketaPersist` → projects store).
 *
 * **Graceful degradation:** если `indexedDB === undefined` либо open
 * выбрасывает (private browsing / quota) — функции возвращают `undefined` для
 * load и no-op для save/clear (`console.warn` в DEV). Wiring в Container Step 12
 * trust'ит этот контракт: hydration cascade fallback к blank, save errors
 * не блокируют UI.
 *
 * **Key:** anketaType (string). Один draft на тип; новый setDraft перезаписывает.
 *
 * Reference: Block_12_hotfix2C_2026-05-17_start.md §6 (Phase C5 Step 11).
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import type { CalculatorAnketaType } from '@pomoshnik/shared';
import type { AnketaState } from '../types';

export const DRAFTS_DB_NAME = 'calculator-drafts';
export const DRAFTS_DB_VERSION = 1;
export const DRAFTS_STORE = 'drafts';

export interface CalculatorDraftsDBSchema extends DBSchema {
  drafts: {
    key: CalculatorAnketaType;
    value: AnketaState;
  };
}

let dbPromise: Promise<IDBPDatabase<CalculatorDraftsDBSchema>> | null = null;

function isIdbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function logWarn(scope: string, err: unknown): void {
  if (import.meta.env.DEV) {
    console.warn(`[calculatorIdbSync] ${scope} failed`, err);
  }
}

/**
 * Singleton — первый вызов открывает (и кэширует) connection. Subsequent calls
 * возвращают cached Promise. На terminate / blocking — cache сбрасывается.
 *
 * Возвращает `null` если IDB недоступен (private browsing, quota exhausted на
 * open). Callers обрабатывают `null` как graceful degradation (no-op для save,
 * undefined для load).
 */
async function getDb(): Promise<IDBPDatabase<CalculatorDraftsDBSchema> | null> {
  if (!isIdbAvailable()) return null;
  if (!dbPromise) {
    dbPromise = openDB<CalculatorDraftsDBSchema>(DRAFTS_DB_NAME, DRAFTS_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(DRAFTS_STORE)) {
          db.createObjectStore(DRAFTS_STORE);
        }
      },
      terminated() {
        dbPromise = null;
      },
      blocking() {
        dbPromise?.then((db) => db.close()).catch(() => {});
        dbPromise = null;
      },
    });
  }
  try {
    return await dbPromise;
  } catch (err) {
    logWarn('openDB', err);
    dbPromise = null;
    return null;
  }
}

/**
 * Persist draft state в IDB под ключом anketaType. На IDB unavailable —
 * no-op (warn в DEV). Не throw'ит наружу — callers просто не блокируют UI.
 */
export async function saveDraftToIdb(
  anketaType: CalculatorAnketaType,
  state: AnketaState,
): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.put(DRAFTS_STORE, state, anketaType);
  } catch (err) {
    logWarn('saveDraftToIdb', err);
  }
}

/**
 * Загружает draft state по anketaType. Возвращает `undefined` если нет записи
 * либо IDB unavailable.
 */
export async function loadDraftFromIdb(
  anketaType: CalculatorAnketaType,
): Promise<AnketaState | undefined> {
  const db = await getDb();
  if (!db) return undefined;
  try {
    return await db.get(DRAFTS_STORE, anketaType);
  } catch (err) {
    logWarn('loadDraftFromIdb', err);
    return undefined;
  }
}

/**
 * Удаляет draft для типа (e.g. после успешного backend persist либо явного
 * сброса анкеты пользователем).
 */
export async function clearDraftFromIdb(anketaType: CalculatorAnketaType): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.delete(DRAFTS_STORE, anketaType);
  } catch (err) {
    logWarn('clearDraftFromIdb', err);
  }
}

/**
 * Полная очистка drafts store (e.g. logout). НЕ удаляет DB и не трогает
 * другие хранилища (saved projects в `pomoshnik-offline-v1` остаются).
 */
export async function clearAllDraftsFromIdb(): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.clear(DRAFTS_STORE);
  } catch (err) {
    logWarn('clearAllDraftsFromIdb', err);
  }
}

/** Для тестов: сбрасывает singleton connection. */
export function _resetDbCacheForTests(): void {
  dbPromise = null;
}
